import { Upload, ImageIcon, Music, Video, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type UploadType = "image" | "audio" | "video";

interface UploadDropzoneProps {
  type: UploadType;
  label: string;
  hint?: string;
  accept: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading?: boolean;
  disabled?: boolean;
  testId?: string;
}

const typeConfig: Record<UploadType, { icon: typeof Upload; gradient: string; accent: string }> = {
  image: {
    icon: ImageIcon,
    gradient: "from-violet-50 to-fuchsia-50 dark:from-violet-950/30 dark:to-fuchsia-950/30",
    accent: "text-violet-500 dark:text-violet-400",
  },
  audio: {
    icon: Music,
    gradient: "from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30",
    accent: "text-blue-500 dark:text-blue-400",
  },
  video: {
    icon: Video,
    gradient: "from-rose-50 to-orange-50 dark:from-rose-950/30 dark:to-orange-950/30",
    accent: "text-rose-500 dark:text-rose-400",
  },
};

export function UploadDropzone({
  type,
  label,
  hint,
  accept,
  onChange,
  isUploading = false,
  disabled = false,
  testId,
}: UploadDropzoneProps) {
  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <label
      className={cn(
        "group relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 transition-all duration-200 cursor-pointer",
        "bg-gradient-to-br", config.gradient,
        "border-muted-foreground/20 hover:border-primary/50 hover:shadow-sm",
        (disabled || isUploading) && "opacity-60 pointer-events-none",
      )}
    >
      <div className={cn(
        "flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border transition-transform duration-200 group-hover:scale-110",
      )}>
        {isUploading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <Icon className={cn("h-5 w-5", config.accent)} />
        )}
      </div>

      <div className="text-center">
        <span className="text-sm font-medium text-foreground/80">
          {isUploading ? "Uploading..." : label}
        </span>
        {hint && (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        )}
      </div>

      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={onChange}
        disabled={disabled || isUploading}
        data-testid={testId}
      />
    </label>
  );
}
